import { LOCALES, type Locale } from '@chess-uno/shared-protocol'
import { de } from './i18n/de.js'
import { en } from './i18n/en.js'
import { es } from './i18n/es.js'
import { fr } from './i18n/fr.js'
import { hi } from './i18n/hi.js'

const CATALOGS = { fr, en, es, de, hi }

export const siteUrl = 'https://chess-uno.com'

/**
 * Indexable pages. Each exists in French (historical URLs, at the root) and in
 * every other language under its own prefix (/en, /es, /de, /hi); all the
 * versions point to each other (hreflang).
 */
export type PageKey = 'home' | 'rules' | 'legal' | 'privacy'

export const PAGE_PATHS: Record<PageKey, Record<Locale, string>> = {
  home: { fr: '/', en: '/en', es: '/es', de: '/de', hi: '/hi' },
  rules: { fr: '/rules', en: '/en/rules', es: '/es/reglas', de: '/de/regeln', hi: '/hi/rules' },
  legal: { fr: '/mentions-legales', en: '/en/legal-notice', es: '/es/aviso-legal', de: '/de/impressum', hi: '/hi/legal-notice' },
  privacy: { fr: '/confidentialite', en: '/en/privacy', es: '/es/privacidad', de: '/de/datenschutz', hi: '/hi/privacy' },
}

export const PAGE_META: Record<PageKey, Record<Locale, { title: string; description: string }>> = {
  home: {
    fr: {
      title: 'Chess-Uno : jeu d’échecs avec cartes en ligne',
      description: 'Jouez à Chess-Uno, une variante d’échecs où une carte révélée change chaque tour. Défiez un ami, jouez en classé ou contre l’ordinateur.',
    },
    en: {
      title: 'Chess-Uno: chess with cards, online',
      description: 'Play Chess-Uno, a chess variant where a revealed card changes every turn. Challenge a friend, play ranked games or practice against the computer.',
    },
    es: {
      title: 'Chess-Uno: ajedrez con cartas, en línea',
      description: 'Juega a Chess-Uno, el ajedrez con cartas: una carta revelada cambia cada turno. Reta a un amigo, juega partidas clasificatorias o contra el ordenador.',
    },
    de: {
      title: 'Chess-Uno: Schach mit Karten, online',
      description: 'Spiel Chess-Uno, Schach mit Karten: Eine aufgedeckte Karte verändert jede Runde. Fordere Freunde heraus, spiel gewertete Partien oder gegen den Computer.',
    },
    hi: {
      title: 'Chess-Uno: कार्ड वाला शतरंज, ऑनलाइन',
      description: 'Chess-Uno खेलें, शतरंज का एक नया रूप जिसमें हर बारी एक खुला कार्ड खेल बदल देता है। दोस्त को चुनौती दें, रैंक्ड गेम खेलें या कंप्यूटर के साथ अभ्यास करें।',
    },
  },
  rules: {
    fr: {
      title: 'Règles de Chess-Uno : échecs et cartes expliqués',
      description: 'Découvrez les règles de Chess-Uno : pioche, cartes spéciales, modes Puriste et Chasse au roi, échec et mat et prise du roi.',
    },
    en: {
      title: 'Chess-Uno rules: chess and cards explained',
      description: 'Learn the rules of Chess-Uno: drawing cards, special cards, the Purist and King Hunt modes, checkmate and king capture.',
    },
    es: {
      title: 'Reglas de Chess-Uno: ajedrez y cartas explicados',
      description: 'Descubre las reglas de Chess-Uno: el robo de cartas, las cartas especiales, los modos de juego, el jaque mate y la captura del rey.',
    },
    de: {
      title: 'Chess-Uno-Regeln: Schach und Karten erklärt',
      description: 'Lerne die Regeln von Chess-Uno: Karten ziehen, Sonderkarten, die beiden Spielmodi, Schachmatt und das Schlagen des Königs.',
    },
    hi: {
      title: 'Chess-Uno के नियम: शतरंज और कार्ड, आसान भाषा में',
      description: 'Chess-Uno के पूरे नियम जानें: हर बारी कार्ड खींचना, स्किप, रिवर्स, +2, +4 और जोकर कार्ड, दोनों गेम मोड, शह और मात और विरोधी राजा को मारना।',
    },
  },
  legal: {
    fr: { title: 'Mentions légales | Chess-Uno', description: 'Mentions légales de Chess-Uno : éditeur du site, hébergement, contact et propriété intellectuelle du jeu d’échecs avec cartes.' },
    en: { title: 'Legal notice | Chess-Uno', description: 'Legal notice of Chess-Uno: publisher of the website, hosting, contact and intellectual property of the chess game with cards.' },
    es: { title: 'Aviso legal | Chess-Uno', description: 'Aviso legal de Chess-Uno: editor del sitio web, alojamiento, contacto y propiedad intelectual del juego de ajedrez con cartas.' },
    de: { title: 'Impressum | Chess-Uno', description: 'Impressum von Chess-Uno: Herausgeber der Website, Hosting, Kontakt und geistiges Eigentum am Schachspiel mit Karten.' },
    hi: { title: 'कानूनी सूचना | Chess-Uno', description: 'Chess-Uno की कानूनी सूचना: वेबसाइट के प्रकाशक, होस्टिंग, संपर्क और कार्ड वाले इस शतरंज गेम की बौद्धिक संपदा की जानकारी।' },
  },
  privacy: {
    fr: {
      title: 'Politique de confidentialité | Chess-Uno',
      description: 'Découvrez quelles données Chess-Uno utilise pour les comptes, les parties et le classement.',
    },
    en: {
      title: 'Privacy policy | Chess-Uno',
      description: 'Find out which data Chess-Uno uses for accounts, games and the ranking.',
    },
    es: {
      title: 'Política de privacidad | Chess-Uno',
      description: 'Descubre qué datos utiliza Chess-Uno para las cuentas, las partidas y la clasificación.',
    },
    de: {
      title: 'Datenschutzerklärung | Chess-Uno',
      description: 'Erfahre, welche Daten Chess-Uno für Konten, Partien und die Rangliste verwendet.',
    },
    hi: {
      title: 'गोपनीयता नीति | Chess-Uno',
      description: 'जानें कि Chess-Uno अकाउंट, गेम और रैंकिंग के लिए कौन-सा डेटा इस्तेमाल करता है।',
    },
  },
}

/** Pages that are not indexed (games, settings, profiles…). */
export const APP_META: Record<Locale, { title: string; description: string }> = {
  fr: { title: 'Jouer | Chess-Uno', description: 'Jouez à Chess-Uno en ligne.' },
  en: { title: 'Play | Chess-Uno', description: 'Play Chess-Uno online.' },
  es: { title: 'Jugar | Chess-Uno', description: 'Juega a Chess-Uno en línea.' },
  de: { title: 'Spielen | Chess-Uno', description: 'Spiel Chess-Uno online.' },
  hi: { title: 'खेलें | Chess-Uno', description: 'Chess-Uno ऑनलाइन खेलें।' },
}

/** Version shown to searchers whose language is none of the site's: English. */
export const X_DEFAULT: Locale = 'en'

export const OG_LOCALES: Record<Locale, string> = { fr: 'fr_FR', en: 'en_US', es: 'es_ES', de: 'de_DE', hi: 'hi_IN' }

/** Words of the game's schema.org description. */
const SCHEMA_TEXTS: Record<Locale, { genre: string[]; platform: string }> = {
  fr: { genre: ['Variante des échecs', 'Jeu de cartes', 'Jeu de stratégie'], platform: 'Navigateur web' },
  en: { genre: ['Chess variant', 'Card game', 'Strategy game'], platform: 'Web browser' },
  es: { genre: ['Variante del ajedrez', 'Juego de cartas', 'Juego de estrategia'], platform: 'Navegador web' },
  de: { genre: ['Schachvariante', 'Kartenspiel', 'Strategiespiel'], platform: 'Webbrowser' },
  hi: { genre: ['शतरंज का रूप', 'कार्ड गेम', 'रणनीति गेम'], platform: 'वेब ब्राउज़र' },
}

/** The indexable page at `pathname`, and its language (null for the app pages). */
export function publicPageAt(pathname: string): { key: PageKey; locale: Locale } | null {
  for (const key of Object.keys(PAGE_PATHS) as PageKey[]) {
    for (const locale of Object.keys(PAGE_PATHS[key]) as Locale[]) {
      if (PAGE_PATHS[key][locale] === pathname) return { key, locale }
    }
  }
  return null
}

/** A link target in the given language: public pages have one URL per language, the others are shared. */
export function localizedPath(path: string, locale: Locale): string {
  const page = publicPageAt(path)
  return page ? PAGE_PATHS[page.key][locale] : path
}

/** Markdown versions of pages, for LLMs and other text readers (see llms.txt). */
export const MARKDOWN_PATHS: Partial<Record<PageKey, Record<Locale, string>>> = {
  rules: { fr: '/rules.md', en: '/en/rules.md', es: '/es/rules.md', de: '/de/rules.md', hi: '/hi/rules.md' },
}

/** schema.org data of a public page (empty when the page has none). */
export function structuredData(key: PageKey, locale: Locale): object[] {
  const t = CATALOGS[locale]
  const url = `${siteUrl}${PAGE_PATHS[key][locale]}`
  const home = `${siteUrl}${PAGE_PATHS.home[locale]}`
  const game = {
    '@type': 'VideoGame',
    '@id': `${siteUrl}/#game`,
    name: 'Chess-Uno',
    url: home,
    description: PAGE_META.home[locale].description,
    inLanguage: [...LOCALES],
    genre: SCHEMA_TEXTS[locale].genre,
    gamePlatform: SCHEMA_TEXTS[locale].platform,
    applicationCategory: 'GameApplication',
    operatingSystem: 'Any',
    playMode: ['SinglePlayer', 'MultiPlayer'],
    numberOfPlayers: { '@type': 'QuantitativeValue', value: 2 },
    image: `${siteUrl}/apple-touch-icon.png`,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  }
  switch (key) {
    case 'home':
      return [
        { '@context': 'https://schema.org', '@type': 'WebSite', '@id': `${siteUrl}/#website`, name: 'Chess-Uno', url: home, inLanguage: locale },
        { '@context': 'https://schema.org', ...game },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          inLanguage: locale,
          mainEntity: t.landing.faq.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      ]
    case 'rules':
      return [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: PAGE_META.rules[locale].title,
          description: PAGE_META.rules[locale].description,
          inLanguage: locale,
          url,
          about: game,
          isPartOf: { '@id': `${siteUrl}/#website` },
        },
      ]
    default:
      return []
  }
}
